<template>
  <CDialog
    disable-outer-close
    v-bind="{ show }"
    @close="close"
    header-style="!sticky !top-0"
    :title="$t('application_for_buy')"
    custom-class="max-w-[600px]"
    disable-scroll
  >
    <div
      class="p-5"
      :class="{
        'max-h-[400px] overflow-y-auto bg-white rounded-xl': scrollContent,
      }"
    >
      <div class="flex flex-col items-center justify-center gap-4 mb-6">
        <div class="p-4 border border-gray-800 rounded-2xl">
          <h4 class="text-dark mb-2 text-base font-semibold leading-normal">
            {{ $t("scan_qr_code") }}
          </h4>
          <p class="text-xs font-normal leading-130 text-[#535E69]">
            {{ $t("scan_qr_code_text") }}
          </p>
        </div>
        <div class="flex flex-col items-center justify-center">
          <h4 class="text-dark mb-2 text-base font-semibold leading-normal">
            {{ $t("amount_to_be_transfered") }}
          </h4>
          <div class="flex items-end gap-1">
            <span class="text-2xl text-green font-semibold">{{
              data?.amountWithCommission
            }}</span>
            <span class="text-sm mb-1">{{ data?.title }}</span>
          </div>
          <p class="text-sm mt-2">
            {{ $t("wallet_deposit_in", { deposit: data?.title }) }}
          </p>
        </div>
        <div class="w-[200px] h-[200px] qr-code p-2 shrink-0 mx-auto">
          <img :src="data?.qr_code" :alt="data?.title" />
        </div>
      </div>
      <div
        class="flex-y-center gap-2 p-4 border border-gray-800 rounded-2xl line-clamp-1"
      >
        <p>{{ $t("address") }}:</p>
        <CCopy
          title-class="!text-dark"
          :title="data?.address"
          :text="data?.address"
          button-class="flex-y-center gap-1"
          tooltip-class="!left-[-60px] !top-[-3px]"
        />
      </div>
      <FGroup
        :label="$t('screenshot_or_check_about_payment')"
        class="mt-4 mb-2"
      >
        <ImageUploaderDefault
          @change="form.values.image = $event"
          :error="form.$v.value.image?.$error"
        />
      </FGroup>
      <CButton
        :text="$t('I_did_payment')"
        class="w-full my-6"
        size="sm"
        :disabled="isLoading"
        :loading="isLoading"
        @click="transferAmount"
      />
    </div>
  </CDialog>
</template>

<script setup lang="ts">
import FGroup from "@/components/Form/FGroup.vue";
import ImageUploaderDefault from "@/components/Form/Uploader/ImageUploaderDefault.vue";
import ApiService from "@/services/ApiService";
import { useAuthStore } from "@/modules/Auth/stores";
import CDialog from "@/components/Common/Dialog/CDialog.vue";
import CButton from "@/components/Common/CButton.vue";
import { useCustomToast } from "@/composables/useCustomToast";
import { useI18n } from "vue-i18n";
import { onMounted, ref } from "vue";
import CCopy from "@/components/Common/CCopy.vue";
import { useForm } from "@/composables/useForm";
import { required } from "@vuelidate/validators";

interface Props {
  show: boolean;
  data: {
    id: number;
    address: string;
    icon: string;
    qr_code: string;
    title: string;
    amount: number;
    amountWithCommission: number;
  };
}
interface Emits {
  (e: "close"): void;
}
const emit = defineEmits<Emits>();

const props = defineProps<Props>();
const scrollContent = ref(false);

const authStore = useAuthStore();
const { showToast } = useCustomToast();
const { t } = useI18n();

const form = useForm(
  {
    image: "",
  },
  {
    image: { required },
  },
);
const isLoading = ref(false);

const close = () => {
  form.values.image = "";
  form.$v.value.$reset();
  emit("close");
};

const transferAmount = () => {
  form.$v.value.$touch();
  if (!form.$v.value.$invalid) {
    isLoading.value = true;
    const formData = new FormData();
    if (form.values.image === "") {
      showToast(t("dont_uploaded"), "error");
    }
    formData.append("amount", props?.data?.amount);
    formData.append("category", props?.data?.id);
    formData.append("image", form.values.image);
    ApiService.post("/payment/fill-balance/", formData)
      .then(() => {
        showToast(t("successfully_send"), "success");
        authStore.fetchUserData();
        close();
      })
      .catch((err) => {
        showToast(`${err.message}`, "error");
        return new Error(err);
      })
      .finally(() => {
        isLoading.value = false;
      });
  }
};

onMounted(() => {
  scrollContent.value = window.innerHeight <= 850;
});
</script>

<style scoped>
.qr-code {
  border-radius: 16px;
  border: 2px solid rgba(22, 204, 83, 0.5);
}

canvas {
  display: inline-block;
  width: 82px;
  height: 82px;
}
</style>
